/**
 * Free Tier Dashboard
 * Main container for free tier user monitoring dashboard
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Users, ArrowLeft, RefreshCw } from 'lucide-react';
import { FreeTierDashboardData, UserDetailResponse, TimeRange, TIME_RANGE_OPTIONS } from './types';
import { fetchFreeTierDashboard, fetchFreeTierUserDetail } from './api';
import { SummaryCards } from './components/SummaryCards';
import { CostChart } from './components/CostChart';
import { UseCaseCostBreakdown } from './components/UseCaseCostBreakdown';
import { CostByModelChart } from './components/CostByModelChart';
import { CostByUser } from './components/CostByUser';
import { UseCaseRunsChart } from './components/UseCaseRunsChart';
import { UserList } from './components/UserList';
import { UserDetail } from './components/UserDetail';

const BRAND_PRIMARY = '#03e8d3';

export const FreeTierDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [dashboardData, setDashboardData] = useState<FreeTierDashboardData | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userDetail, setUserDetail] = useState<UserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await fetchFreeTierDashboard(timeRange);
    if (apiError) {
      setError(apiError);
    } else {
      setDashboardData(data);
    }
    setLoading(false);
  };

  const fetchUser = async (userId: number) => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await fetchFreeTierUserDetail(userId, timeRange);
    if (apiError) {
      setError(apiError);
    } else {
      setUserDetail(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedUserId) {
      fetchUser(selectedUserId);
    } else {
      fetchDashboard();
    }
  }, [timeRange, selectedUserId]);

  const handleUserSelect = (userId: number) => {
    setSelectedUserId(userId);
    setUserDetail(null);
  };

  const handleBackToOverview = () => {
    setSelectedUserId(null);
    setUserDetail(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: BRAND_PRIMARY }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-medium">Error loading dashboard data</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={selectedUserId ? () => fetchUser(selectedUserId) : fetchDashboard}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedUserId && (
            <button
              onClick={handleBackToOverview}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: BRAND_PRIMARY }} />
              {selectedUserId ? 'Free Tier User Details' : 'Free Tier User Monitoring'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {selectedUserId
                ? `Viewing activity for user ID: ${selectedUserId}`
                : 'Monitor what free tier users are doing'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={selectedUserId ? () => fetchUser(selectedUserId) : fetchDashboard}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh data"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
            <span className="text-sm font-semibold text-gray-700">Time Range</span>
          </div>
          <div className="flex gap-2">
            {TIME_RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setTimeRange(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  timeRange === option.value
                    ? 'text-white shadow-sm'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                style={timeRange === option.value ? { backgroundColor: BRAND_PRIMARY } : {}}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {selectedUserId && userDetail ? (
        <UserDetail data={userDetail} timeRange={timeRange} />
      ) : dashboardData ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <SummaryCards summary={dashboardData.summary} />

          {/* Row 1: Use Case Cost Breakdown | Cost Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <UseCaseCostBreakdown data={dashboardData.usecase_cost_breakdown} />
            <CostChart costData={dashboardData.cost_data} granularity={dashboardData.granularity} />
          </div>

          {/* Row 2: Use Case Runs | Cost by Model */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <UseCaseRunsChart
              data={dashboardData.usecase_run_counts}
              costData={dashboardData.usecase_cost_breakdown}
            />
            <CostByModelChart data={dashboardData.cost_data.cost_by_model} />
          </div>

          {/* Cost by User */}
          <CostByUser data={dashboardData.cost_by_user} onUserSelect={handleUserSelect} />

          {/* User List */}
          <UserList users={dashboardData.users} onUserSelect={handleUserSelect} />
        </div>
      ) : null}
    </div>
  );
};

export default FreeTierDashboard;
